import { titleCase } from 'src/utils/titleCase/titleCase';
import { formatSnakeCaseStatus } from 'src/components/DynamicCriteriaGenerator/components/CRMStatus/config';

/**
 * Resolves a composite CRM status ID (e.g. "DEAL-h_c_p_adoption") to a user-configured label.
 * Falls back to formatted status value if not found in the label map.
 */
const resolveCrmStatusLabel = (
  compositeId: string,
  statusLabelMap?: Map<string, string>
): string => {
  // Format is "{crm_type}-{crm_status}" — split on first hyphen only
  const dashIndex = compositeId.indexOf('-');
  const statusValue = dashIndex >= 0 ? compositeId.slice(dashIndex + 1) : compositeId;

  if (statusLabelMap?.has(statusValue)) {
    return statusLabelMap.get(statusValue)!;
  }

  return formatSnakeCaseStatus(statusValue);
};

/**
 * Formats execution parameters into a user-friendly readable string
 * @param executionParams - Object containing execution parameters
 * @param statusLabelMap - Optional map from MedRM status value to user-configured label
 * @returns Formatted string representation of the parameters
 */
export const formatExecutionParams = (
  executionParams?: Record<string, any>,
  statusLabelMap?: Map<string, string>
): string => {
  if (!executionParams || Object.keys(executionParams).length === 0) {
    return '';
  }
  const formattedParts: string[] = [];
  const processedKeys = new Set<string>();

  // Extract year range values if present
  const minYear = executionParams.min_year ?? executionParams.minYear;
  const maxYear = executionParams.max_year ?? executionParams.maxYear;
  const codeSetName = executionParams.code_set?.label;
  const filterSetName = executionParams.filter_set?.label;

  // Handle code set and filter set labels
  if (codeSetName) {
    formattedParts.push(`Code Set: ${codeSetName}`);
  }
  if (filterSetName) {
    formattedParts.push(`Filter Set: ${filterSetName}`);
  }

  // Handle CRM statuses with label lookup
  if (Array.isArray(executionParams.crm_statuses)) {
    const labels = executionParams.crm_statuses.map((id: string) =>
      resolveCrmStatusLabel(id, statusLabelMap)
    );
    formattedParts.push(`Statuses: ${labels.join(', ')}`);
    processedKeys.add('crm_statuses');
  }

  // Handle year range first if present
  if (minYear !== undefined || maxYear !== undefined) {
    if (minYear !== undefined && maxYear !== undefined) {
      if (minYear === maxYear) {
        formattedParts.push(`Year: ${minYear}`);
      } else {
        formattedParts.push(`Year Range: ${minYear}-${maxYear}`);
      }
    } else if (minYear !== undefined) {
      formattedParts.push(`Year Range: ${minYear}+`);
    } else if (maxYear !== undefined) {
      formattedParts.push(`Year Range: up to ${maxYear}`);
    }

    // Mark these keys as processed
    processedKeys.add('min_year');
    processedKeys.add('minYear');
    processedKeys.add('max_year');
    processedKeys.add('maxYear');
  }

  for (const [key, value] of Object.entries(executionParams)) {
    if (
      value === null ||
      value === undefined ||
      processedKeys.has(key) ||
      key === 'filter_set' ||
      key === 'code_set' ||
      key === 'provider_ids'
    )
      continue;

    // Convert camelCase or snake_case to readable format
    const readableKey = key
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();

    // Handle special cases
    if (typeof value === 'boolean') {
      formattedParts.push(`${readableKey}: ${value ? 'Yes' : 'No'}`);
    } else if (key === 'type') {
      formattedParts.push(
        `${readableKey}: ${titleCase(value.split('_').join(' '))}`
      );
    } else if (Array.isArray(value)) {
      formattedParts.push(`${readableKey}: ${value.join(', ')}`);
    } else if (typeof value === 'object') {
      formattedParts.push(`${readableKey}: ${JSON.stringify(value)}`);
    } else if (typeof value === 'string') {
      // Convert string values to readable format
      const readableValue = titleCase(value.split('_').join(' '));
      formattedParts.push(`${readableKey}: ${readableValue}`);
    } else {
      formattedParts.push(`${readableKey}: ${value}`);
    }
  }

  return formattedParts.join(' • ');
};
